import React, { useMemo } from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Where from './Where.js';
import TransportationMethod from './TransportationMethod.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlane, faShip, faTruck, faXmark } from '@fortawesome/free-solid-svg-icons';

const DEBUG = false;

function LegEditor(props) {

    const {
        seaPorts, canBeEmpty,
        routes, setRoutes,
        incoterm, setIncoterm,
        comments, setComments,
        transportationMethod, setTransportationMethod,
        errors = [], autoPopulateSource,
        unlocode, width,
        organization, accesstoken,
        disabledTransportationMethods, disabledTransportationMethodsTooltip,
    } = props;

    // Validate routes: if one field is filled, the other must also be filled
    const validationErrors = routes.reduce((acc, route, index) => {
        const hasSource = route.source && route.source.text && route.source.text.trim() !== '';
        const hasDestination = route.destination && route.destination.text && route.destination.text.trim() !== '';
        
        if (hasSource && !hasDestination) {
            acc.push(`where:${index}:address-destination`);
        }
        if (hasDestination && !hasSource) {
            acc.push(`where:${index}:address-source`);
        }
        return acc;
    }, []);

    const allErrors = [...errors, ...validationErrors];

    return (
        <div style={ width ? { width: width } : {}}>
            <div style={{ paddingTop: !!setTransportationMethod ? '16px' : '0px' }}>
                {!!transportationMethod && !!setTransportationMethod && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {transportationMethod === 'sea' && ( <b><FontAwesomeIcon icon={faShip} />&nbsp;By Sea</b>)}
                        {transportationMethod === 'air' && ( <b><FontAwesomeIcon icon={faPlane} />&nbsp;By Air</b>)}
                        {transportationMethod === 'land' && ( <b><FontAwesomeIcon icon={faTruck} />&nbsp;By Land</b>)}
                        &nbsp;
                        &nbsp;
                        <Button
                            onClick={() => setTransportationMethod(null)}
                            variant='text' color='primary' sx={{ textTransform: 'none', textDecoration: 'underline', margin: '0px', padding: '0px' }}
                        >
                            Change
                        </Button>
                    </Box>
                )}
                {!transportationMethod && (
                    <>
                        <TransportationMethod
                            disabled={!setTransportationMethod}
                            transportationMethod={transportationMethod}
                            setTransportationMethod={(v) => {
                                setTransportationMethod(v);
                            }}
                            disabledMethods={disabledTransportationMethods}
                            disabledMethodsTooltip={disabledTransportationMethodsTooltip}
                        />
                        <Box sx={{ mt: '10px', px: '8px', fontSize: '12px', lineHeight: 1.5, color: '#64748b' }}>
                            <b>By Land</b> — road transport: a standalone port-to-door drayage/cartage quote (containers &amp; LCL) or domestic trucking. For the road leg of a sea/air shipment, choose Sea or Air and add the trucking as a cost line instead.
                        </Box>
                    </>
                )}
                {errors.includes('transportationMethod') && (
                    <Alert fullWidth severity='error' sx={{ marginTop: '12px' }}>
                        Please pick a transporation method.
                    </Alert>
                )}
            </div>
            {(DEBUG || transportationMethod) && (
                <>
                    <div>
                        {routes.map((r, i) => (
                            <div key={JSON.stringify(r)} style={{ marginTop: !!setTransportationMethod ? '16px' : '0px', display: 'flex', alignItems: 'center' }}>
                                <div style={{ flex: 1 }} >
                                    <Where 
                                        transportationMethod={transportationMethod}
                                        errors={allErrors.map(e => e.replace(`where:${i}:`, 'address-'))}
                                        source={r.source}
                                        destination={r.destination}
                                        setDestination={v => {
                                            const newRoutes = JSON.parse(JSON.stringify(routes));
                                            newRoutes[i].destination = v;
                                            setRoutes(newRoutes);
                                        }}
                                        setSource={v => {
                                            const newRoutes = JSON.parse(JSON.stringify(routes));
                                            newRoutes[i].source = v;
                                            setRoutes(newRoutes);
                                        }}
                                        seaPorts={seaPorts}
                                        unlocode={unlocode}
                                        organization={organization}
                                        accesstoken={accesstoken}
                                        enableDoorLocations={!!organization?.integrations?.doorRates?.enabled}
                                    />
                                </div>
                                <div style={{ flex: 0 }}>
                                    <IconButton
                                        color='error'
                                        onClick={() => {
                                            const newRoutes = JSON.parse(JSON.stringify(routes));
                                            newRoutes.splice(i, 1);
                                            setRoutes(newRoutes);
                                        }}
                                        disabled={!canBeEmpty && routes.length === 1}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </IconButton>
                                </div>
                            </div>
                        ))}
                    </div>
                    <Button
                        onClick={() => {
                            const newRoutes = JSON.parse(JSON.stringify(routes));
                            const lastRoute = newRoutes.length > 0 ? newRoutes[newRoutes.length - 1] : null;
                            newRoutes.push({ source: autoPopulateSource && lastRoute ? lastRoute.destination : null, destination: null });
                            setRoutes(newRoutes);
                        }}
                        variant='outlined'
                        color='primary'
                        sx={{ marginTop: '12px', borderStyle: 'dashed', padding: '12px 0px' }}
                        fullWidth
                    >
                        + Route
                    </Button>
                    { setIncoterm || setComments && <hr style={{ opacity: 0.3 }} /> }
                    { setIncoterm && (
                        <FormControl fullWidth sx={{ marginBottom: '18px', marginTop: '18px' }}>
                            <InputLabel htmlFor='commerce-terms'>Commerce Terms</InputLabel>
                            <Select
                                fullWidth
                                label={'Commerce Terms'}
                                id={`commerce-terms`}
                                value={incoterm}
                                onChange={(event: SelectChangeEvent) => setIncoterm(event.target.value) }
                            >
                                {transportationMethod === 'sea' && <MenuItem value={'EXW'}>EXW: Ex Works</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'FCA'}>FCA: Free Carrier</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'CPT'}>CPT: Carriage Paid To</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'CIP'}>CIP: Carriage and Insurance Paid To</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'DAP'}>DAP: Delivered at Place</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'DPU'}>DPU: Delivered at Place Unloaded</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'DDP'}>DDP: Delivered Duty Paid</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'FAS'}>FAS: Free Alongside Ship</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'FOB'}>FOB: Free on Board</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'CFR'}>CFR: Cost and Freight</MenuItem>}
                                {transportationMethod === 'sea' && <MenuItem value={'CIF'}>CIF: Cost, Insurance, and Freight</MenuItem>}
                                {transportationMethod === 'land' && <MenuItem value={'EXW'}>EXW: Ex Works</MenuItem>}
                                {transportationMethod === 'land' && <MenuItem value={'FCA'}>FCA: Free Carrier</MenuItem>}
                                {transportationMethod === 'land' && <MenuItem value={'CPT'}>CPT: Carriage Paid To</MenuItem>}
                                {transportationMethod === 'land' && <MenuItem value={'CIP'}>CIP: Carriage and Insurance Paid To</MenuItem>}
                                {transportationMethod === 'land' && <MenuItem value={'DAP'}>DAP: Delivered at Place</MenuItem>}
                                {transportationMethod === 'land' && <MenuItem value={'DPU'}>DPU: Delivered at Place Unloaded</MenuItem>}
                                {transportationMethod === 'land' && <MenuItem value={'DDP'}>DDP: Delivered Duty Paid</MenuItem>}
                                {transportationMethod === 'air' && <MenuItem value={'CPT'}>CPT: Carriage Paid To</MenuItem>}
                                {transportationMethod === 'air' && <MenuItem value={'CIF'}>CIF: Cost, Insurance, and Freight</MenuItem>}
                                {transportationMethod === 'air' && <MenuItem value={'CIP'}>CIP: Carriage and Insurance Paid To</MenuItem>}
                                {transportationMethod === 'air' && <MenuItem value={'DAP'}>DAP: Delivered at Place</MenuItem>}
                                {transportationMethod === 'air' && <MenuItem value={'DDP'}>DDP: Delivered Duty Paid</MenuItem>}
                            </Select>
                        </FormControl>
                    )}
                    {setComments && (
                        <FormControl variant='outlined' fullWidth>
                            <TextField
                                InputLabelProps={{ shrink: true }}
                                maxRows={4}
                                label={'Team\'s Notes'}
                                multiline
                                placeholder=''
                                id='product-comments'
                                value={comments}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => { setComments(event.target.value) }}
                            />
                        </FormControl>
                    )}
                </>
            )}
        </div>
    )
}

export default LegEditor;
